/**
 * Особисті налаштування на сервері (PRF-01; `GET/PATCH /api/v1/me/preferences`, company-settings.md).
 * Налаштування людини діють на всіх її пристроях, а `localStorage` (`prefs.ts`) лишається кешем для
 * першого кадру без блимання.
 *
 * - **Старт.** Після входу `startPrefsSync()` викликають маршрут «/» (разом із `/meta`: від
 *   налаштувань залежить стартова сторінка) і оболонка (коли прийшов `/me`). Модуль — у першому
 *   екрані: окремий чанк був би ще одним запитом і ще одним кроком перед стартовою сторінкою
 *   (бюджет запитів, performance-budget.md, розд. 3). Відповідь сервера перекриває кеш:
 *   власне значення людини, інакше типове компанії (`defaults`). Поле, яке людина змінила ще до
 *   відповіді, не перекривається: її вибір піде на сервер.
 * - **Зміна** будь-де (меню, палітра, профіль) — `setPrefs` → `PATCH` лише змінених полів з
 *   `If-Match: "<version>"`. Запити йдуть по одному; зміни, зроблені поки запит у дорозі, їдуть
 *   наступним. `412` (змінили на іншому пристрої) — перечитати й повторити свою зміну один раз.
 * - **«Як у компанії»** — `resetPrefs([поле])`: `null` на сервері, у кеші — типове компанії.
 * - **Збій збереження** — тост «Не вдалося зберегти» і повернення попереднього значення (PRF-01).
 * - Без відповіді сервера (немає API, мережа, mock-API S4) налаштування живуть лише в кеші: сервер не
 *   питаємо вдруге до наступного входу і нічого не відкочуємо.
 */
import type {
  MePreferencesPatch,
  MePreferencesResponse,
  PersonalPreferences,
  SettingsPatchResponse,
} from '@clientline/contracts';
import { useSyncExternalStore } from 'react';
import { prefsText as t } from '../i18n/uk/prefs.ts';
import { ApiProblem, api } from '../platform/api.ts';
import { toast } from '../ui/notify/store.ts';
import { applyServerPrefs, onPersonPrefs, type Prefs, setPrefs } from './prefs.ts';

/** Поля, які веб тримає в кеші (`prefs.ts`) і синхронізує з `/me/preferences`. */
export const SYNCED = [
  'theme',
  'density',
  'singleKeys',
  'keyHints',
  'startPage',
  'weekStart',
] as const;
export type SyncedField = (typeof SYNCED)[number];

/** Поле сервера, якого немає в кеші пристрою: «привітання показано» (UX-38) — дані людини, не пристрою. */
type ServerOnlyField = 'welcomed';
type Patch = { -readonly [K in SyncedField | ServerOnlyField]?: PersonalPreferences[K] | null };

export interface ServerPrefs {
  status: 'idle' | 'loading' | 'ready' | 'off';
  /** Власні значення людини (з урахуванням змін у дорозі); відсутнє поле — «як у компанії». */
  own: Partial<Pick<PersonalPreferences, SyncedField>>;
  defaults: PersonalPreferences | null;
  /** Чи пропонує компанія легкий режим на слабкій машині (ADM-804). */
  liteModeOffer: boolean;
  /** Привітання першого входу показано людині; `null` — сервер ще не відповів (або його немає). */
  welcomed: boolean | null;
}

let server: MePreferencesResponse | null = null;
let status: ServerPrefs['status'] = 'idle';
/** Ще не відправлені зміни: поле → значення, `null` — «як у компанії». */
let pending: Patch = {};
/** Відправлені, відповіді ще немає. */
let sending: Patch | null = null;
/** Збереження, після якого людина чекає тост «Збережено» (зміни з профілю, а не з меню). */
let notifyNext = false;
let generation = 0;
/** Людина закрила привітання в цьому документі: не повертаємо діалог, навіть якщо збереження не вдалося. */
let welcomedHere = false;
let snapshot: ServerPrefs = computeSnapshot();
const listeners = new Set<() => void>();

const isSynced = (key: string): key is SyncedField => (SYNCED as readonly string[]).includes(key);

/** Значення в кеші за відповіддю сервера: власне, інакше типове компанії. */
function effective(field: SyncedField, value: PersonalPreferences[SyncedField] | null | undefined) {
  if (value !== null && value !== undefined) return value;
  const fallback = server?.defaults[field];
  // Стартова «/» компанії — це «як у /meta»: у кеші порожньо, вибір робить home.tsx.
  if (field === 'startPage') return fallback && fallback !== '/' ? fallback : '';
  return fallback;
}

function computeSnapshot(): ServerPrefs {
  const own: Record<string, unknown> = {};
  for (const field of SYNCED) {
    const value =
      field in pending
        ? pending[field]
        : sending && field in sending
          ? sending[field]
          : server?.values[field];
    if (value !== null && value !== undefined) own[field] = value;
  }
  return {
    status,
    own: own as ServerPrefs['own'],
    defaults: server?.defaults ?? null,
    liteModeOffer: server?.liteModeOffer ?? true,
    welcomed: server
      ? welcomedHere ||
        (pending.welcomed ?? sending?.welcomed ?? server.values.welcomed ?? false) === true
      : null,
  };
}

function emit() {
  snapshot = computeSnapshot();
  for (const l of listeners) l();
}

/** Значення сервера → кеш, крім полів, які людина вже змінила й ще не зберегла. */
function applyToCache() {
  if (!server) return;
  const patch: Partial<Prefs> = {};
  for (const field of SYNCED) {
    if (field in pending || (sending && field in sending)) continue;
    const value = effective(field, server.values[field]);
    if (value !== undefined) (patch as Record<string, unknown>)[field] = value;
  }
  applyServerPrefs(patch);
}

async function load(gen: number): Promise<boolean> {
  try {
    const body = await api<MePreferencesResponse>('GET', '/api/v1/me/preferences');
    if (gen !== generation) return false;
    server = body;
    status = 'ready';
    applyToCache();
    emit();
    return true;
  } catch {
    if (gen !== generation) return false;
    if (!server) {
      status = 'off';
      pending = {};
      emit();
    }
    return false;
  }
}

let started: Promise<void> = Promise.resolve();

/** Зміна людиною → поля сервера: порожня стартова в кеші — «як у компанії» (`null`). */
function toSynced(patch: Partial<Prefs>): Patch {
  const synced: Patch = {};
  for (const [key, value] of Object.entries(patch)) {
    if (!isSynced(key)) continue;
    (synced as Record<string, unknown>)[key] = key === 'startPage' && value === '' ? null : value;
  }
  return synced;
}

/**
 * Почати синхронізацію після входу (оболонка, стартовий маршрут). Повторний виклик повертає той
 * самий проміс: «/» чекає налаштувань разом із метаданими. Проміс не відхиляється ніколи.
 */
export function startPrefsSync(): Promise<void> {
  if (status !== 'idle') return started;
  status = 'loading';
  emit();
  started = load(generation).then((ok) => {
    if (ok) void flush();
  });
  return started;
}

function queue(patch: Patch, notify = false) {
  if (status === 'off' || status === 'idle') return;
  pending = { ...pending, ...patch };
  notifyNext ||= notify;
  emit();
  void flush();
}

async function flush(retried = false): Promise<void> {
  if (!server || sending || Object.keys(pending).length === 0) return;
  const gen = generation;
  const body = pending;
  const notify = notifyNext;
  pending = {};
  notifyNext = false;
  sending = body;
  try {
    const result = await api<SettingsPatchResponse>('PATCH', '/api/v1/me/preferences', {
      body: body satisfies MePreferencesPatch,
      ifMatch: server.version,
    });
    if (gen !== generation) return;
    const values = { ...server.values } as Record<string, unknown>;
    for (const [field, value] of Object.entries(body)) {
      if (value === null) delete values[field];
      else values[field] = value;
    }
    server = { ...server, version: result.version, values: values as typeof server.values };
    sending = null;
    if (notify) toast({ text: t.prefsSaved });
  } catch (error) {
    if (gen !== generation) return;
    sending = null;
    if (error instanceof ApiProblem && error.status === 412 && !retried) {
      // Профіль змінили на іншому пристрої: свіжа версія, і та сама зміна ще раз — поверх неї.
      pending = { ...body, ...pending };
      notifyNext ||= notify;
      await load(gen);
      if (gen === generation) return flush(true);
      return;
    }
    if (!(error instanceof ApiProblem && error.status === 401)) {
      toast({ text: t.prefsSaveFailed, tone: 'alert' });
    }
    // Попереднє значення — те, що знає сервер (або новіша зміна людини, що чекає в черзі).
    applyToCache();
  } finally {
    if (gen === generation) {
      emit();
      void flush();
    }
  }
}

// Зміна людиною в цій вкладці — на сервер. Поля без синхронізації (пристрій) сюди не приходять.
onPersonPrefs((patch) => {
  const synced = toSynced(patch);
  if (Object.keys(synced).length > 0) queue(synced);
});

/**
 * Зміна з профілю: те саме, що `setPrefs`, але з тостом «Збережено» після відповіді (автозбереження,
 * PRF-01). Без сервера — лише кеш, як і з меню.
 */
export function savePrefs(patch: Partial<Pick<Prefs, SyncedField>>) {
  notifyNext = status === 'ready' || status === 'loading';
  // У чергу запит ставить слухач `onPersonPrefs`; значення не змінилось — тосту не чекаємо.
  setPrefs(patch);
  if (Object.keys(pending).length === 0 && !sending) notifyNext = false;
}

/**
 * Привітання закрито («Почати роботу», `Esc`): позначка йде на сервер, у `localStorage` її немає.
 * Без відповіді сервера нічого не пишемо — привітання тоді й не показується.
 */
export function markWelcomed() {
  welcomedHere = true;
  queue({ welcomed: true });
  emit();
}

/** «Як у компанії» для полів; без полів — «Скинути все» (PRF-01). */
export function resetPrefs(fields: readonly SyncedField[] = SYNCED) {
  if (!server) return;
  const patch: Patch = {};
  const local: Partial<Prefs> = {};
  for (const field of fields) {
    patch[field] = null;
    const value = effective(field, null);
    if (value !== undefined) (local as Record<string, unknown>)[field] = value;
  }
  queue(patch, true);
  applyServerPrefs(local);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Підписка поза React (рішення «вантажити чанк чи ні» до `import()`, `shell/use-welcome-due.ts`). */
export const subscribeServerPrefs = subscribe;

export function getServerPrefs(): ServerPrefs {
  return snapshot;
}

export function useServerPrefs(): ServerPrefs {
  return useSyncExternalStore(subscribe, getServerPrefs, getServerPrefs);
}

/** Для тестів: дочекатися запитів у дорозі. */
export async function settlePrefsSync() {
  for (let i = 0; i < 20 && (sending || status === 'loading'); i++) {
    await new Promise((r) => setTimeout(r, 10));
  }
}

/** Для тестів і виходу: забути стан сервера; відповіді в дорозі ігноруються. */
export function resetPrefsSync() {
  generation++;
  welcomedHere = false;
  server = null;
  status = 'idle';
  pending = {};
  sending = null;
  notifyNext = false;
  started = Promise.resolve();
  emit();
}
